import type { UtmData } from '~/schemas/utm'
import { utmKeys, utmSchema } from '~/schemas/utm'

const LAST_TOUCH_COOKIE = 'orderUtm'
const FIRST_TOUCH_COOKIE = 'firstUtm'
const UTM_COOKIE_MAX_AGE = 60 * 60 * 24 * 30

const readSingleValue = (value: unknown) => {
  const candidate = Array.isArray(value) ? value[0] : value

  return typeof candidate === 'string' && candidate.trim() !== ''
    ? candidate.trim()
    : undefined
}

const pickUtmValues = (source: Record<string, unknown>) => {
  return utmSchema.parse(
    Object.fromEntries(
      utmKeys.map((key) => [key, readSingleValue(source[key])]),
    ),
  )
}

export const hasUtmValues = (utm?: UtmData | null) => {
  return !!utm && Object.keys(utm).length > 0
}

export const useOrderUtm = () => {
  // No `default`: with one, every anonymous SSR response set two `{}` cookies,
  // which stops the HTML from ever being cached (BOAT-2101). Readers below
  // treat a missing cookie as "no touch".
  const cookieOptions = {
    maxAge: UTM_COOKIE_MAX_AGE,
    sameSite: 'lax' as const,
  }

  const lastTouchCookie = useCookie<UtmData | null>(LAST_TOUCH_COOKIE, cookieOptions)
  const firstTouchCookie = useCookie<UtmData | null>(FIRST_TOUCH_COOKIE, cookieOptions)

  /**
   * A tagged arrival replaces the last touch as a WHOLE SET, and writes the
   * first touch only when we have never recorded one.
   *
   * The previous per-key merge (`incoming[key] ?? current[key]`) could store a
   * combination that never happened: arriving on google/cpc and returning via
   * klaviyo left `source=klaviyo, medium=cpc`. Replacing wholesale means every
   * stored pair is one that actually occurred.
   *
   * First touch is write-once on purpose — it is what stops a later touch (a
   * Klaviyo recovery email, an internal upsell) taking credit for an
   * acquisition it did not make.
   */
  const captureFromQuery = (query: Record<string, unknown>) => {
    const incoming = pickUtmValues(query)

    if (!hasUtmValues(incoming)) return lastTouchCookie.value ?? {}

    lastTouchCookie.value = incoming

    if (!hasUtmValues(firstTouchCookie.value)) {
      firstTouchCookie.value = incoming
    }

    return lastTouchCookie.value ?? {}
  }

  const lastTouch = computed(() => lastTouchCookie.value ?? {})
  const firstTouch = computed(() => firstTouchCookie.value ?? {})

  /**
   * The touch pair in the shape every API capture point accepts.
   *
   * Email is the only identifier that survives a device switch, so the touches
   * have to travel with every point that hands the API an address — the cart,
   * login and registration, not just the order (BOAT-1892). An empty touch is
   * omitted rather than sent as `{}`: an untagged visitor is a fact, and the
   * API treats "absent" and "empty" the same way, so there is nothing to gain
   * by sending noise.
   */
  const utmPayload = () => {
    return {
      ...(hasUtmValues(lastTouch.value) ? { utm: lastTouch.value } : {}),
      ...(hasUtmValues(firstTouch.value)
        ? { first_touch_utm: firstTouch.value }
        : {}),
    }
  }

  return {
    captureFromQuery,
    firstTouch,
    lastTouch,
    utmPayload,
    /** @deprecated use `lastTouch` — kept so existing call sites keep working. */
    utm: lastTouch,
  }
}
